/**
 * Mongo Migration Export — temporary, super-admin-only.
 *
 * Three-phase flow (build-then-download):
 *   1. POST /api/admin/mongo-migration/jobs          → returns {job_id}
 *   2. GET  /api/admin/mongo-migration/jobs/{id}     → poll progress
 *   3. GET  /api/admin/mongo-migration/jobs/{id}/download → final ZIP
 *
 * The endpoint is gated server-side by ENABLE_MONGO_MIGRATION_EXPORT=1;
 * the UI surfaces 503 / 403 responses verbatim so the operator knows
 * what to fix. Designed to be deleted along with the backend module
 * after the one-time data migration is complete.
 */
import { useEffect, useRef, useState } from "react";
import {
  Database,
  DownloadSimple,
  Warning,
  CheckCircle,
  ShieldCheck,
  Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 1500;

function _humanBytes(n) {
  if (!Number.isFinite(n)) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function _authHeaders() {
  try {
    const saved = localStorage.getItem("usair_user");
    const email = saved ? JSON.parse(saved)?.email : "";
    return email ? { "X-User-Email": email } : {};
  } catch {
    return {};
  }
}

async function _readError(resp) {
  try {
    const j = await resp.json();
    return j?.detail || resp.statusText || "Unknown error";
  } catch {
    return resp.statusText || "Unknown error";
  }
}

export default function MongoMigrationExportPage({ user, onLogout, onLogoutAll }) {
  const [phase, setPhase] = useState("idle");   // idle | building | downloading | done
  const [job, setJob] = useState(null);         // latest job poll payload
  const [downloaded, setDownloaded] = useState(null); // { filename, bytes, at }
  const [error, setError] = useState(null);     // { status, detail }
  const pollRef = useRef(null);

  // Stop polling on unmount.
  useEffect(() => () => {
    if (pollRef.current) clearTimeout(pollRef.current);
  }, []);

  async function _pollOnce(jobId) {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/mongo-migration/jobs/${encodeURIComponent(jobId)}`,
        { credentials: "include", headers: _authHeaders() },
      );
      if (!r.ok) {
        const detail = await _readError(r);
        setError({ status: r.status, detail });
        setPhase("idle");
        toast.error(`Status check failed (HTTP ${r.status})`);
        return;
      }
      const data = await r.json();
      setJob(data);

      if (data.status === "ready") {
        await _downloadZip(jobId);
      } else if (data.status === "failed") {
        setError({
          status: 0,
          detail: data.error_kind || "Server-side build failed",
        });
        setPhase("idle");
        toast.error("Export build failed");
      } else {
        // Keep polling.
        pollRef.current = setTimeout(() => _pollOnce(jobId), POLL_MS);
      }
    } catch (e) {
      setError({ status: 0, detail: e?.message || "Network error" });
      setPhase("idle");
    }
  }

  async function _downloadZip(jobId) {
    setPhase("downloading");
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/mongo-migration/jobs/${encodeURIComponent(jobId)}/download`,
        { credentials: "include", headers: _authHeaders() },
      );
      if (!r.ok) {
        const detail = await _readError(r);
        setError({ status: r.status, detail });
        setPhase("idle");
        toast.error(`Download failed (HTTP ${r.status})`);
        return;
      }
      const cd = r.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const filename = m ? m[1] : "usair-mongo-export.zip";

      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);

      setDownloaded({
        filename,
        bytes: blob.size,
        at: new Date().toISOString(),
      });
      setPhase("done");
      toast.success(`Exported ${filename} (${_humanBytes(blob.size)})`);
    } catch (e) {
      setError({ status: 0, detail: e?.message || "Network error" });
      setPhase("idle");
    }
  }

  async function startExport() {
    if (phase !== "idle") return;
    setError(null);
    setDownloaded(null);
    setJob(null);
    setPhase("building");
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/mongo-migration/jobs`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ..._authHeaders() },
        },
      );
      if (!r.ok) {
        const detail = await _readError(r);
        setError({ status: r.status, detail });
        setPhase("idle");
        toast.error(`Export failed (HTTP ${r.status})`);
        return;
      }
      const data = await r.json();
      _pollOnce(data.job_id);
    } catch (e) {
      setError({ status: 0, detail: e?.message || "Network error" });
      setPhase("idle");
    }
  }

  const busy = phase !== "idle" && phase !== "done";
  const pctColls = job && job.collections_total > 0
    ? Math.min(100, Math.round((job.collections_done / job.collections_total) * 100))
    : null;
  const pctDocs = job && job.docs_total > 0
    ? Math.min(100, Math.round((job.docs_done / job.docs_total) * 100))
    : null;
  // Prefer doc-level progress when we have a denominator; fall back to
  // collection-level progress otherwise.
  const pct = pctDocs !== null ? pctDocs : pctColls;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mongo-migration-page">
      <AdminPageHeader
        title="Mongo Migration Export"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-4">
        {/* Banner — temporary tool, security model */}
        <div
          data-testid="mm-banner"
          className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-[12px] text-amber-900 leading-snug"
        >
          <div className="flex items-start gap-2">
            <ShieldCheck size={14} weight="bold" className="mt-0.5 text-amber-700" />
            <div>
              <b>Temporary migration utility — super-admin only.</b>
              <div className="mt-1">
                The server builds the full ZIP first (manifest.json plus one
                collections/&lt;name&gt;.jsonl per Mongo collection, including
                GridFS), then sends it as a single download. Progress below
                tracks the server-side build.
              </div>
              <div className="mt-1">
                The endpoint is also gated by{" "}
                <code>ENABLE_MONGO_MIGRATION_EXPORT=1</code> on the server.
                If it isn&apos;t set you&apos;ll see a 503 below.
              </div>
            </div>
          </div>
        </div>

        {/* Action card */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="px-3 py-2 border-b border-slate-200 flex items-center gap-2">
            <Database size={14} weight="bold" className="text-slate-500" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex-1">
              Export now
            </h2>
          </div>

          <div className="p-4 space-y-3">
            <p className="text-[12px] text-slate-600 leading-relaxed">
              The export runs on the server. Keep this tab open while it
              builds — when it&apos;s ready the download starts automatically.
            </p>

            <button
              onClick={startExport}
              disabled={busy}
              data-testid="mm-export-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DownloadSimple size={12} weight="bold" />
              {phase === "building" && "Building on server…"}
              {phase === "downloading" && "Downloading…"}
              {phase === "done" && "Start export"}
              {phase === "idle" && "Start export"}
            </button>

            {/* Build progress */}
            {phase === "building" && (
              <div data-testid="mm-progress" className="mt-2">
                <div className="h-2 w-full bg-slate-100 rounded-sm overflow-hidden">
                  <div
                    className="h-2 bg-emerald-600 transition-all duration-200"
                    style={{ width: pct !== null ? `${pct}%` : "33%" }}
                  />
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] font-mono text-slate-600">
                  <Spinner size={11} weight="bold" className="animate-spin text-emerald-700" />
                  {job ? (
                    <span data-testid="mm-progress-text">
                      {job.collections_done} / {job.collections_total} collections
                      {job.docs_total > 0 && (
                        <> · {job.docs_done.toLocaleString()} / {job.docs_total.toLocaleString()} docs ({pctDocs}%)</>
                      )}
                      {job.current_collection && (
                        <> · <span className="text-slate-500">{job.current_collection}</span></>
                      )}
                    </span>
                  ) : (
                    <span>Starting build…</span>
                  )}
                </div>
              </div>
            )}

            {/* Downloading */}
            {phase === "downloading" && (
              <div data-testid="mm-downloading"
                   className="mt-2 flex items-center gap-2 text-[11px] font-mono text-slate-600">
                <Spinner size={11} weight="bold" className="animate-spin text-emerald-700" />
                Build complete — downloading
                {job?.zip_size ? <> {_humanBytes(job.zip_size)}</> : null}…
              </div>
            )}

            {/* Last successful download */}
            {downloaded && phase === "done" && (
              <div
                data-testid="mm-last-success"
                className="mt-3 bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-[12px] text-emerald-900"
              >
                <div className="flex items-start gap-2">
                  <CheckCircle size={14} weight="bold" className="mt-0.5 text-emerald-700" />
                  <div>
                    <b>Export complete.</b>
                    <div className="mt-0.5 font-mono text-[11px] break-all">
                      {downloaded.filename}
                    </div>
                    <div className="text-[11px] text-emerald-800">
                      {_humanBytes(downloaded.bytes)} · {new Date(downloaded.at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && !busy && (
              <div
                data-testid="mm-error"
                className="mt-3 bg-red-50 border border-red-200 rounded-sm p-3 text-[12px] text-red-900"
              >
                <div className="flex items-start gap-2">
                  <Warning size={14} weight="bold" className="mt-0.5 text-red-700" />
                  <div>
                    <b>
                      Export failed
                      {error.status ? ` (HTTP ${error.status})` : ""}.
                    </b>
                    <div className="mt-0.5 break-words">
                      {error.detail || "Unknown error"}
                    </div>
                    {error.status === 503 && (
                      <div className="mt-1 text-[11px] text-red-800">
                        Set <code>ENABLE_MONGO_MIGRATION_EXPORT=1</code> on
                        the backend, restart, and try again.
                      </div>
                    )}
                    {error.status === 403 && (
                      <div className="mt-1 text-[11px] text-red-800">
                        Only super-admins can run this export.
                      </div>
                    )}
                    {error.status === 429 && (
                      <div className="mt-1 text-[11px] text-red-800">
                        Another export is already running on this server.
                        Wait for it to finish and try again.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reminder footer */}
        <div className="text-[11px] text-slate-500 px-1">
          Tip: remove this page (and the matching backend module) once the
          one-time migration to the new deployment is complete.
        </div>
      </main>
    </div>
  );
}
